import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { AreaIcon, type AreaIconName } from '../components/AreaIcon';
import { useI18n } from '../i18n';
import { AUTOMATIONS, type AutomationDetail, type AutomationMode } from './about-automations';
import { AUTOMATION_MAP, MODE_ORDER, SOURCES, SOURCE_ORDER, type AutomationMapContent, type SourceId, type Story, type StoryScreen } from './automation-map-content';

type Item = AutomationDetail & { index: number; from: SourceId[] };

/** Showcase of the automations: a live flow map, guided scenarios and the chained log. Dark, whatever the theme. */
export function AutomationMapPage() {
  const { t, locale, setLocale } = useI18n();
  const m = AUTOMATION_MAP[locale];
  const d = AUTOMATIONS[locale];
  const items: Item[] = useMemo(() => d.items.map((it, index) => ({ ...it, index, from: SOURCES[index]?.from ?? [] })), [d]);
  const [open, setOpen] = useState<Item | null>(null);
  const poster = useSearchParams()[0].get('affiche');

  if (poster === 'portrait' || poster === 'paysage') return <Poster m={m} d={d} items={items} format={poster} />;

  return (
    <div className="am">
      <header className="am-top">
        <Link to="/about" className="am-brand" aria-label={m.back}>
          <img src="/logo-dark.svg" alt="Syndiqia" height={24} width={101} />
        </Link>
        <div className="am-top-actions">
          <button type="button" className="am-link" onClick={() => setLocale(locale === 'fr' ? 'en' : 'fr')}>
            {t.language}
          </button>
          <Link to="/about" className="am-link">
            {m.back}
          </Link>
        </div>
      </header>

      <Hero m={m} items={items} />
      <FlowMap m={m} d={d} items={items} onOpen={setOpen} />
      <Stories m={m} />
      <Chain m={m} />

      <section className="am-closing">
        <h2>{m.closingTitle}</h2>
        <p>{m.closingText}</p>
        <Link to="/about#automatisations" className="am-button am-button-primary">
          {m.closingCta}
        </Link>
      </section>

      {open && <Drawer m={m} d={d} item={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

const ORBIT: AreaIconName[] = ['requests', 'dues', 'maintenance', 'compliance', 'governance', 'reservations', 'documents'];

function Hero({ m, items }: { m: AutomationMapContent; items: Item[] }) {
  const areas = new Set(items.map((i) => i.icon)).size;
  return (
    <section className="am-hero">
      <div className="am-aurora" aria-hidden="true" />
      <div className="am-hero-inner">
        <div className="am-hero-text">
          <p className="am-eyebrow">{m.eyebrow}</p>
          <h1>
            <span>{m.title[0]}</span>
            <span className="am-gradient">{m.title[1]}</span>
          </h1>
          <p className="am-lead">{m.lead}</p>
          <a href="#carte" className="am-button am-button-primary">
            {m.explore}
          </a>
          <dl className="am-stats">
            <Stat value={items.length} label={m.stats.automations} />
            <Stat value={areas} label={m.stats.areas} />
            <Stat value={3} label={m.stats.modes} />
            <Stat value={100} suffix=" %" label={m.stats.logged} />
          </dl>
        </div>
        <div className="am-orbit" aria-hidden="true">
          <div className="am-ring am-ring-1" />
          <div className="am-ring am-ring-2" />
          <div className="am-ring am-ring-3" />
          <div className="am-orbit-spin">
            {ORBIT.map((icon, i) => (
              <span key={icon} className="am-orbit-item" style={{ '--a': `${(360 / ORBIT.length) * i}deg` } as React.CSSProperties}>
                <span className="am-orbit-tile">
                  <AreaIcon name={icon} />
                </span>
              </span>
            ))}
          </div>
          <div className="am-core">
            <img src="/favicon.svg" alt="" width={84} height={84} />
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ value, label, suffix = '' }: { value: number; label: string; suffix?: string }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return setN(value);
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / 1400);
      setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value]);
  return (
    <div className="am-stat">
      <dt>
        {n}
        {suffix}
      </dt>
      <dd>{label}</dd>
    </div>
  );
}

interface Edge {
  id: string;
  d: string;
  mode: AutomationMode;
  index: number;
}

function FlowMap({ m, d, items, onOpen, poster = false }: { m: AutomationMapContent; d: typeof AUTOMATIONS.fr; items: Item[]; onOpen?: (i: Item) => void; poster?: boolean }) {
  const [filter, setFilter] = useState<AutomationMode | 'all'>('all');
  const [focus, setFocus] = useState<number | null>(null);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const box = useRef<HTMLDivElement>(null);
  const nodes = useRef(new Map<string, HTMLElement>());
  const ref = useCallback((key: string) => (el: HTMLElement | null) => {
    if (el) nodes.current.set(key, el);
    else nodes.current.delete(key);
  }, []);

  const groups = MODE_ORDER.map((mode) => ({
    mode,
    items: items.filter((i) => i.mode === mode).sort((a, b) => SOURCE_ORDER.indexOf(a.from[0]!) - SOURCE_ORDER.indexOf(b.from[0]!)),
  }));

  // Curves between the cards, measured from the layout (the map is HTML; the SVG only draws the links).
  useLayoutEffect(() => {
    const container = box.current;
    if (!container) return;
    const measure = () => {
      const c = container.getBoundingClientRect();
      const at = (key: string, side: 'l' | 'r') => {
        const r = nodes.current.get(key)?.getBoundingClientRect();
        if (!r) return null;
        return { x: (side === 'l' ? r.left : r.right) - c.left, y: r.top + r.height / 2 - c.top };
      };
      const curve = (a: { x: number; y: number }, b: { x: number; y: number }) => {
        const k = Math.max(40, (b.x - a.x) * 0.5);
        return `M${a.x.toFixed(1)},${a.y.toFixed(1)} C${(a.x + k).toFixed(1)},${a.y.toFixed(1)} ${(b.x - k).toFixed(1)},${b.y.toFixed(1)} ${b.x.toFixed(1)},${b.y.toFixed(1)}`;
      };
      const next: Edge[] = [];
      for (const it of items) {
        const left = at(`a${it.index}`, 'l');
        const right = at(`a${it.index}`, 'r');
        const gate = at(`g${it.mode}`, 'l');
        if (!left || !right || !gate) continue;
        for (const s of it.from) {
          const src = at(`s${s}`, 'r');
          if (src) next.push({ id: `${s}-${it.index}`, d: curve(src, left), mode: it.mode, index: it.index });
        }
        next.push({ id: `${it.index}-${it.mode}`, d: curve(right, gate), mode: it.mode, index: it.index });
      }
      setEdges(next);
      setSize({ w: c.width, h: c.height });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    void document.fonts?.ready.then(measure);
    return () => observer.disconnect();
  }, [items]);

  const active = (i: Item) => (filter === 'all' || filter === i.mode) && (focus === null || focus === i.index);
  const sourceActive = (s: SourceId) => items.some((i) => i.from.includes(s) && active(i));
  const gateActive = (mode: AutomationMode) => items.some((i) => i.mode === mode && active(i));

  const map = (
    <div className="am-map" ref={box}>
      <svg className="am-links" width={size.w} height={size.h} aria-hidden="true">
        {edges.map((e) => {
          const it = items[e.index]!;
          const on = active(it);
          return (
            <g key={e.id} className={`am-link-${e.mode}${on ? ' is-on' : ''}${focus === e.index ? ' is-focus' : ''}`}>
              <path className="am-link-path" d={e.d} />
              {on && !poster && (
                <circle className="am-packet" r={focus === e.index ? 3.5 : 2.4}>
                  <animateMotion key={e.d} dur={`${3.2 + ((e.index * 7) % 10) / 4}s`} begin={`${-((e.index * 13) % 17) / 5}s`} repeatCount="indefinite" path={e.d} />
                </circle>
              )}
            </g>
          );
        })}
      </svg>

      <div className="am-col am-col-sources">
        <p className="am-col-title">{m.columns.sources}</p>
        {SOURCE_ORDER.map((s) => (
          <div key={s} ref={ref(`s${s}`)} className={`am-source${sourceActive(s) ? '' : ' is-dim'}`}>
            <SourceGlyph id={s} />
            <span>
              <strong>{m.sources[s].title}</strong>
              <small>{m.sources[s].sub}</small>
            </span>
          </div>
        ))}
      </div>

      <div className="am-col am-col-auto">
        <p className="am-col-title">{m.columns.syndiqia}</p>
        {groups.map((g) => (
          <div key={g.mode} className={`am-group am-mode-${g.mode}`}>
          <div className="am-band">
            {g.items.map((it, i) => (
              <button
                key={it.index}
                type="button"
                ref={ref(`a${it.index}`)}
                className={`am-node${active(it) ? '' : ' is-dim'}`}
                style={{ '--i': i } as React.CSSProperties}
                tabIndex={poster ? -1 : undefined}
                onMouseEnter={() => setFocus(it.index)}
                onMouseLeave={() => setFocus(null)}
                onFocus={() => setFocus(it.index)}
                onBlur={() => setFocus(null)}
                onClick={() => onOpen?.(it)}
              >
                {/* Phones: where the work comes from, shown on the card (the links are drawn only on wide screens). */}
                <span className="am-node-src" aria-hidden="true">
                  {it.from.map((s) => (
                    <SourceGlyph key={s} id={s} />
                  ))}
                </span>
                <AreaIcon name={it.icon} />
                <span className="am-node-text">
                  <strong>{it.title}</strong>
                  <small>{it.value}</small>
                </span>
                <span className="am-dot" aria-hidden="true" />
              </button>
            ))}
          </div>
          {/* Phones: the group flows into its "who decides" card, right below it. */}
          <div className={`am-gate am-gate-m${gateActive(g.mode) ? '' : ' is-dim'}`}>
            <span className="am-gate-ring" aria-hidden="true">
              <span className="am-dot" />
            </span>
            <strong>{m.gates[g.mode].title}</strong>
            <small>{m.gates[g.mode].text}</small>
          </div>
          </div>
        ))}
      </div>

      <div className="am-col am-col-gates">
        <p className="am-col-title">{m.columns.you}</p>
        {groups.map((g) => (
          <div key={g.mode} className="am-gate-slot">
            <div ref={ref(`g${g.mode}`)} className={`am-gate am-mode-${g.mode}${gateActive(g.mode) ? '' : ' is-dim'}`}>
              <span className="am-gate-ring" aria-hidden="true">
                <span className="am-dot" />
              </span>
              <strong>{m.gates[g.mode].title}</strong>
              <small>{m.gates[g.mode].text}</small>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (poster) return map;

  return (
    <section className="am-section" id="carte">
      <div className="am-wrap">
        <h2 className="am-h2">{m.mapTitle}</h2>
        <p className="am-section-lead">{m.mapLead}</p>
        <div className="am-filters" role="group">
          {(['all', ...MODE_ORDER] as const).map((f) => (
            <button key={f} type="button" className={`am-chip${filter === f ? ' is-on' : ''}${f !== 'all' ? ` am-mode-${f}` : ''}`} aria-pressed={filter === f} onClick={() => setFilter(f)}>
              {f !== 'all' && <span className="am-dot" />}
              {f === 'all' ? m.all : d.modes[f]}
            </button>
          ))}
        </div>

        {map}
      </div>
    </section>
  );
}

/** Fixed-size poster of the map, exported as PNG/PDF (?affiche=portrait: 4:5 for LinkedIn; paysage: 16:9 for decks). */
function Poster({ m, d, items, format }: { m: AutomationMapContent; d: typeof AUTOMATIONS.fr; items: Item[]; format: 'portrait' | 'paysage' }) {
  return (
    <div className={`am am-poster am-poster-${format}`}>
      <div className="am-aurora" aria-hidden="true" />
      <header className="am-poster-head">
        <p className="am-eyebrow">{m.eyebrow}</p>
        <h1>
          <span>{m.title[0]}</span> <span className="am-gradient">{m.title[1]}</span>
        </h1>
        <p className="am-lead">{m.lead}</p>
      </header>
      <FlowMap m={m} d={d} items={items} poster />
      <footer className="am-poster-foot">
        <img src="/logo-dark.svg" alt="Syndiqia" height={30} width={127} />
        <span>{m.posterNote}</span>
        <span>syndiqia.com</span>
      </footer>
    </div>
  );
}

function SourceGlyph({ id }: { id: SourceId }) {
  const paths: Record<SourceId, React.ReactNode> = {
    inbox: <path d="M3 6.5A2.5 2.5 0 0 1 5.5 4h13A2.5 2.5 0 0 1 21 6.5v11a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 17.5zM4 7l8 6 8-6" />,
    app: <path d="M8 2.5h8A2.5 2.5 0 0 1 18.5 5v14a2.5 2.5 0 0 1-2.5 2.5H8A2.5 2.5 0 0 1 5.5 19V5A2.5 2.5 0 0 1 8 2.5zM10.5 18.5h3" />,
    bank: <path d="M3 9.5 12 4l9 5.5M5 10v7M9.5 10v7M14.5 10v7M19 10v7M3 20h18" />,
    clock: <path d="M12 3a9 9 0 1 1 0 18 9 9 0 0 1 0-18zM12 7.5V12l3 2" />,
    board: <path d="M12 3l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.4 6.8 19.1l1-5.8L3.5 9.2l5.9-.9z" />,
    meeting: <path d="M8 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM16.5 11a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM2.5 19.5c.6-3.2 2.8-5 5.5-5s4.9 1.8 5.5 5M14 14.6c.8-.4 1.6-.6 2.5-.6 2.4 0 4.3 1.6 5 4.5" />,
  };
  return (
    <span className="am-glyph" aria-hidden="true">
      <svg viewBox="0 0 24 24" width="22" height="22">
        {paths[id]}
      </svg>
    </span>
  );
}

function Drawer({ m, d, item, onClose }: { m: AutomationMapContent; d: typeof AUTOMATIONS.fr; item: Item; onClose: () => void }) {
  const close = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    close.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    document.body.classList.add('am-locked');
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.classList.remove('am-locked');
    };
  }, [onClose]);
  const l = d.labels;
  return (
    <div className="am-drawer-backdrop" onClick={onClose}>
      <aside className={`am-drawer am-mode-${item.mode}`} role="dialog" aria-modal="true" aria-label={item.title} onClick={(e) => e.stopPropagation()}>
        <button ref={close} type="button" className="am-drawer-close" onClick={onClose} aria-label={m.close}>
          ×
        </button>
        <div className="am-drawer-head">
          <AreaIcon name={item.icon} />
          <span className="am-badge">
            <span className="am-dot" />
            {d.modes[item.mode]}
          </span>
        </div>
        <h3>{item.title}</h3>
        <p className="am-drawer-value">{item.value}</p>
        <div className="am-compare">
          <div>
            <p className="am-label">{l.before}</p>
            <p>{item.before}</p>
          </div>
          <div>
            <p className="am-label">{l.trigger}</p>
            <p>{item.trigger}</p>
          </div>
        </div>
        <p className="am-label">{l.steps}</p>
        <ol className="am-steps">
          {item.steps.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
        {item.confirm && (
          <>
            <p className="am-label">{l.confirm}</p>
            <p>{item.confirm}</p>
          </>
        )}
        {item.auto && (
          <>
            <p className="am-label">{item.mode === 'always' ? l.always : l.auto}</p>
            <p>{item.auto}</p>
          </>
        )}
        <p className="am-label">{l.guardrails}</p>
        <ul className="am-guards">
          {item.guardrails.map((g) => (
            <li key={g}>{g}</li>
          ))}
        </ul>
      </aside>
    </div>
  );
}

const STEP_MS = 3400;

function Stories({ m }: { m: AutomationMapContent }) {
  const [story, setStory] = useState(0);
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [visible, setVisible] = useState(false);
  const section = useRef<HTMLElement>(null);
  const s: Story = m.stories[story]!;

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) setPlaying(false);
    const el = section.current;
    if (!el) return;
    const observer = new IntersectionObserver(([e]) => setVisible(!!e?.isIntersecting), { threshold: 0.35 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!playing || hovered || !visible) return;
    const timer = window.setTimeout(() => {
      if (step < s.steps.length - 1) setStep(step + 1);
      else {
        setStory((story + 1) % m.stories.length);
        setStep(0);
      }
    }, step === s.steps.length - 1 ? STEP_MS * 1.6 : STEP_MS);
    return () => window.clearTimeout(timer);
  }, [playing, hovered, visible, step, story, s.steps.length, m.stories.length]);

  const current = s.steps[step]!;
  return (
    <section className="am-section am-section-stories" ref={section}>
      <div className="am-wrap">
        <h2 className="am-h2">{m.storiesTitle}</h2>
        <p className="am-section-lead">{m.storiesLead}</p>
        <div className="am-tabs" role="tablist">
          {m.stories.map((st, i) => (
            <button
              key={st.tab}
              type="button"
              role="tab"
              aria-selected={i === story}
              className={`am-tab${i === story ? ' is-on' : ''}`}
              onClick={() => {
                setStory(i);
                setStep(0);
              }}
            >
              <AreaIcon name={st.icon} />
              {st.tab}
            </button>
          ))}
          <button type="button" className="am-play" onClick={() => setPlaying(!playing)} aria-label={playing ? m.pause : m.play}>
            {playing ? '❚❚' : '▶'}
          </button>
        </div>

        <div className="am-story" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
          <ol className="am-timeline">
            {s.steps.map((st, i) => (
              <li key={st.title} className={`am-lane-${st.lane}${i === step ? ' is-on' : ''}${i < step ? ' is-done' : ''}`}>
                <button type="button" onClick={() => setStep(i)}>
                  <span className="am-tl-dot" aria-hidden="true" />
                  <span className="am-tl-lane">{m.lanes[st.lane]}</span>
                  <span className="am-tl-title">{st.title}</span>
                  {i === step && playing && !hovered && visible && <span key={`${story}-${step}`} className="am-tl-progress" style={{ animationDuration: `${i === s.steps.length - 1 ? STEP_MS * 1.6 : STEP_MS}ms` }} />}
                </button>
              </li>
            ))}
          </ol>
          <div className="am-stage">
            <div className="am-stage-glow" aria-hidden="true" />
            <div className="am-stage-head">
              <span className={`am-stage-lane am-lane-${current.lane}`}>{m.lanes[current.lane]}</span>
              <span className="am-stage-title">{s.title}</span>
              <span className="am-stage-example">{m.example}</span>
            </div>
            <div key={`${story}-${step}`} className="am-screen">
              <Screen screen={current.screen} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Screen({ screen }: { screen: StoryScreen }) {
  switch (screen.kind) {
    case 'email':
      return (
        <div className="am-mail">
          <div className="am-mail-row">
            <span className="am-avatar" aria-hidden="true">
              {screen.from.charAt(0)}
            </span>
            <span>
              <strong>{screen.from}</strong>
              <small>→ {screen.to}</small>
            </span>
            <time>{screen.time}</time>
          </div>
          <p className="am-mail-subject">{screen.subject}</p>
          {screen.attachment && <span className="am-attachment">PDF · {screen.attachment}</span>}
          <span className="am-scan" aria-hidden="true" />
        </div>
      );
    case 'clock':
      return (
        <div className="am-clock">
          <span className="am-clock-face" aria-hidden="true">
            <span />
          </span>
          <strong>{screen.time}</strong>
          <p>{screen.text}</p>
        </div>
      );
    case 'chips':
      return (
        <div className="am-chips">
          <div>
            {screen.chips.map((c, i) => (
              <span key={c} className="am-tag" style={{ animationDelay: `${i * 140}ms` }}>
                {c}
              </span>
            ))}
          </div>
          <p>{screen.text}</p>
        </div>
      );
    case 'quote':
      return (
        <div className="am-quote">
          <span className="am-doc-lines" aria-hidden="true">
            <span />
            <span />
            <span className="am-hl" />
            <span />
          </span>
          <blockquote>{screen.quote}</blockquote>
          <cite>{screen.source}</cite>
        </div>
      );
    case 'draft':
      return (
        <div className="am-draft">
          <p className="am-typing">{screen.text}</p>
          <span className="am-cite">{screen.cite}</span>
        </div>
      );
    case 'figures':
      return (
        <div className="am-figures">
          {screen.rows.map(([k, v], i) => (
            <div key={k} className="am-fig-row" style={{ animationDelay: `${i * 160}ms` }}>
              <span>{k}</span>
              <span>{v}</span>
            </div>
          ))}
          <div className="am-fig-row am-fig-total" style={{ animationDelay: `${screen.rows.length * 160}ms` }}>
            <span>{screen.total[0]}</span>
            <span>{screen.total[1]}</span>
          </div>
        </div>
      );
    case 'checks':
      return (
        <ul className="am-checklist">
          {screen.items.map((c, i) => (
            <li key={c} style={{ animationDelay: `${i * 220}ms` }}>
              {c}
            </li>
          ))}
        </ul>
      );
    case 'approve':
      return (
        <div className="am-approve">
          <strong>{screen.title}</strong>
          <p>{screen.text}</p>
          <div className="am-approve-actions">
            <span className="am-approve-yes">{screen.approve}</span>
            <span className="am-approve-no">{screen.edit}</span>
          </div>
          <small>{screen.auto}</small>
        </div>
      );
    case 'log':
      return (
        <div className="am-log">
          {screen.lines.map((line, i) => (
            <p key={line} style={{ animationDelay: `${i * 180}ms` }}>
              {line}
            </p>
          ))}
          <code>#{screen.hash}</code>
        </div>
      );
  }
}

/** Display-only fingerprint (not the real chain): a short, stable hex string per label. */
function shortHash(text: string): string {
  let h = 2166136261;
  for (const ch of text) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  return (h >>> 0).toString(16).padStart(8, '0');
}

function Chain({ m }: { m: AutomationMapContent }) {
  const blocks = m.chain.map((label, i) => ({ label, hash: shortHash(m.chain.slice(0, i + 1).join('|')), prev: i ? shortHash(m.chain.slice(0, i).join('|')) : '00000000' }));
  return (
    <section className="am-section am-section-chain">
      <div className="am-wrap">
        <h2 className="am-h2">{m.chainTitle}</h2>
        <p className="am-section-lead">{m.chainLead}</p>
      </div>
      <div className="am-chain" aria-hidden="true">
        <div className="am-chain-track">
          {[...blocks, ...blocks].map((b, i) => (
            <div key={i} className="am-block">
              <span className="am-block-prev">← {b.prev.slice(0, 6)}</span>
              <strong>{b.label}</strong>
              <code>{b.hash}</code>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
